<script>
/**
 * @component TimelineCronico
 * @description Timeline cronológico de eventos clínicos del paciente
 *              (UC-REG-065). Reemplaza la sección "Actividad reciente"
 *              lineal del SeguimientoTab por una visualización con
 *              swimlanes verticales en eje temporal.
 *
 *              Swimlanes (de arriba a abajo):
 *              - Vitales
 *              - Conversaciones IA (con score)
 *              - Medicación
 *              - Check-ins
 *
 *              Cada evento es un dot coloreado por semáforo (verde/amarillo/
 *              rojo). Click en evento emite `event-click` con el item del
 *              activity stream para que el padre abra un drawer/modal con
 *              detalle.
 *
 *              Implementación: posicionamiento manual con CSS calc + Vue
 *              computed. Sin SVG ni librería de charts para mantener el
 *              componente simple.
 *
 * @example <TimelineCronico :activity="activity" @event-click="onEventClick" />
 */
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

const SWIMLANES = [
  { key: "vitals", label: "Vitales", color: "#1d3557" },
  { key: "conversations", label: "Conversaciones IA", color: "#1d4ed8" },
  { key: "medications", label: "Medicación", color: "#92400e" },
  { key: "checkins", label: "Check-ins", color: "#6b7280" },
];

const RANGE_OPTIONS = [
  { key: "7d", label: "7 días", days: 7 },
  { key: "30d", label: "30 días", days: 30 },
  { key: "90d", label: "90 días", days: 90 },
];

export default {
  name: "TimelineCronico",

  props: {
    /**
     * @description Activity stream del endpoint /patients/:id/monitoring.
     *              Cada item: { type, swimlane, at, semaphore, payload }.
     */
    activity: {
      type: Array,
      default: () => [],
    },
    /**
     * @description Rango temporal a mostrar. Default '30d'.
     */
    initialRange: {
      type: String,
      default: "30d",
    },
  },

  data() {
    return {
      SWIMLANES,
      RANGE_OPTIONS,
      selectedRange: this.initialRange,
      activeSwimlanes: SWIMLANES.map((s) => s.key),
    };
  },

  computed: {
    /**
     * @description Días del rango seleccionado.
     */
    rangeDays() {
      const opt = RANGE_OPTIONS.find((o) => o.key === this.selectedRange);
      return opt ? opt.days : 30;
    },
    /**
     * @description Fecha de inicio del rango.
     */
    rangeStart() {
      const now = new Date();
      return new Date(now.getTime() - this.rangeDays * 24 * 60 * 60 * 1000);
    },
    rangeEnd() {
      return new Date();
    },
    /**
     * @description Activity filtrada por rango + swimlanes activos.
     */
    visibleActivity() {
      const start = this.rangeStart;
      const end = this.rangeEnd;
      return this.activity.filter((ev) => {
        if (!ev.at) return false;
        if (!this.activeSwimlanes.includes(ev.swimlane)) return false;
        const at = new Date(ev.at);
        return at >= start && at <= end;
      });
    },
    /**
     * @description Eventos agrupados por swimlane.
     */
    eventsBySwimlane() {
      const grouped = {};
      for (const lane of SWIMLANES) grouped[lane.key] = [];
      for (const ev of this.visibleActivity) {
        if (grouped[ev.swimlane]) grouped[ev.swimlane].push(ev);
      }
      return grouped;
    },
    /**
     * @description Ticks del eje X — 5 a 8 marcas a lo largo del rango.
     */
    xAxisTicks() {
      const days = this.rangeDays;
      const tickCount = days <= 7 ? days : Math.min(8, Math.ceil(days / 5));
      const tickInterval = days / tickCount;
      const ticks = [];
      for (let i = 0; i <= tickCount; i += 1) {
        const day = i * tickInterval;
        const at = new Date(
          this.rangeEnd.getTime() - (days - day) * 24 * 60 * 60 * 1000
        );
        ticks.push({
          at,
          label: format(at, days <= 7 ? "EEE d" : "d MMM", { locale: es }),
          leftPercent: (day / days) * 100,
        });
      }
      return ticks;
    },
  },

  methods: {
    /**
     * @description Posición % en el eje X de un evento.
     */
    leftPercentFor(at) {
      const t = new Date(at).getTime();
      const start = this.rangeStart.getTime();
      const end = this.rangeEnd.getTime();
      const pct = ((t - start) / (end - start)) * 100;
      return Math.max(0, Math.min(100, pct));
    },
    /**
     * @description Etiqueta tooltip de un evento.
     */
    tooltipFor(ev) {
      const date = format(parseISO(ev.at), "dd MMM HH:mm", { locale: es });
      const name = ev.payload?.name || ev.type;
      if (ev.swimlane === "conversations" && ev.payload?.score != null) {
        return `${date} — ${name} · score ${ev.payload.score}/10`;
      }
      if (ev.swimlane === "vitals" && ev.payload?.value) {
        return `${date} — ${name}: ${ev.payload.value}${
          ev.payload.unit ? " " + ev.payload.unit : ""
        }`;
      }
      return `${date} — ${name}`;
    },
    /**
     * @description Class del dot según semáforo del evento.
     */
    dotClass(ev) {
      return `timeline-dot--${ev.semaphore || "none"}`;
    },
    /**
     * @description Score visible (solo conversations).
     */
    scoreLabel(ev) {
      if (ev.swimlane !== "conversations") return null;
      return ev.payload?.score != null ? `${ev.payload.score}` : null;
    },
    /**
     * @description Valor visible (solo vitals con valor numérico).
     */
    valueLabel(ev) {
      if (ev.swimlane !== "vitals") return null;
      return ev.payload?.value ? String(ev.payload.value) : null;
    },
    onEventClick(ev) {
      this.$emit("event-click", ev);
    },
    toggleSwimlane(key) {
      const idx = this.activeSwimlanes.indexOf(key);
      if (idx >= 0) {
        this.activeSwimlanes = this.activeSwimlanes.filter((k) => k !== key);
      } else {
        this.activeSwimlanes = [...this.activeSwimlanes, key];
      }
    },
  },
};
</script>

<template lang="pug">
  .timeline-cronico
    .timeline-cronico__header
      .timeline-cronico__title Timeline
      .timeline-cronico__controls
        el-radio-group(v-model="selectedRange" size="mini")
          el-radio-button(
            v-for="opt in RANGE_OPTIONS"
            :key="opt.key"
            :label="opt.key"
          ) {{ opt.label }}
        .timeline-cronico__lanes-filter
          span.lanes-toggle(
            v-for="lane in SWIMLANES"
            :key="lane.key"
            :class="{ 'lanes-toggle--off': !activeSwimlanes.includes(lane.key) }"
            @click="toggleSwimlane(lane.key)"
          )
            span.lanes-toggle__dot(:style="{ background: lane.color }")
            span {{ lane.label }}

    .timeline-cronico__body
      .timeline-cronico__lane(
        v-for="lane in SWIMLANES"
        v-if="activeSwimlanes.includes(lane.key)"
        :key="lane.key"
      )
        .lane__label {{ lane.label }}
        .lane__track
          .lane__event(
            v-for="(ev, idx) in eventsBySwimlane[lane.key]"
            :key="`${lane.key}-${idx}`"
            :style="{ left: leftPercentFor(ev.at) + '%' }"
            :title="tooltipFor(ev)"
            @click="onEventClick(ev)"
          )
            span.timeline-dot(:class="dotClass(ev)")
            span.timeline-dot__label(v-if="scoreLabel(ev)") {{ scoreLabel(ev) }}
            span.timeline-dot__label.timeline-dot__label--small(v-if="valueLabel(ev)") {{ valueLabel(ev) }}

      //- Eje X (días)
      .timeline-cronico__axis
        .timeline-cronico__axis-spacer
        .timeline-cronico__axis-track
          .axis-tick(
            v-for="(tick, idx) in xAxisTicks"
            :key="`tick-${idx}`"
            :style="{ left: tick.leftPercent + '%' }"
          ) {{ tick.label }}

    .timeline-cronico__empty(v-if="!visibleActivity.length")
      i.el-icon-info
      span Sin actividad en este período.
</template>

<style lang="scss" scoped>
.timeline-cronico {
  font-family: "Plus Jakarta Sans", sans-serif;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    gap: 12px;
    flex-wrap: wrap;
  }
  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #0f1e36;
  }
  &__controls {
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: wrap;
  }
  &__lanes-filter {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__lane {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 12px;
    align-items: center;
  }

  &__axis {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 12px;
    margin-top: 8px;
    border-top: 1px dashed #e5e7eb;
    padding-top: 8px;
    height: 24px;
  }

  &__axis-spacer {
  }

  &__axis-track {
    position: relative;
    height: 16px;
  }

  &__empty {
    padding: 32px;
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
}

.lanes-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 12px;
  background: #f3f4f6;
  user-select: none;

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  &--off {
    opacity: 0.4;
    text-decoration: line-through;
  }
}

.lane__label {
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  text-align: right;
}

.lane__track {
  position: relative;
  height: 40px;
  background: #fbfcfe;
  border-radius: 8px;
  border: 1px solid #f0f2f7;
}

.lane__event {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;

  &:hover .timeline-dot {
    transform: scale(1.3);
  }
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 0 0 1px rgba(15, 30, 54, 0.2);
  transition: transform 0.1s ease;

  &--red {
    background: #b3261e;
  }
  &--yellow {
    background: #b07c0a;
  }
  &--green {
    background: #1f8a4c;
  }
  &--none {
    background: #9ca3af;
  }
}

.timeline-dot__label {
  font-size: 9px;
  font-weight: 700;
  color: #111827;
  margin-top: 2px;
  white-space: nowrap;
  background: white;
  padding: 0 4px;
  border-radius: 4px;
  border: 1px solid #e5e7eb;

  &--small {
    font-size: 8px;
    font-weight: 600;
  }
}

.axis-tick {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 10px;
  color: #9ca3af;
  white-space: nowrap;
}
</style>
